import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/common/StatusPill";
import { FavouriteStar, ShapeIcons } from "@/components/catalogue/Bits";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { RunSurface } from "@/components/run/RunSurface";
import { WhatNext, RunsPanel } from "@/components/run/Panels";
import { AgentPrompts, SavedPromptPicker } from "@/components/prompts/PromptLists";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { teamColour } from "@/lib/constants";
import { cn } from "@/lib/utils";

export const Block = ({ title, children, testid }) => (
  <section className="card-elevated p-5" data-testid={testid}>
    <h2 className="text-base font-semibold">{title}</h2>
    <div className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export const EntryHeader = ({ entity, id, title, kindLabel, model, shapes, status, canRun, onRun }) => {
  const { teamsById } = useCatalogue();
  const team = teamsById[entity.team];
  return (
    <header className="card-elevated relative mb-6 overflow-hidden p-5 md:p-6" data-testid="entry-header">
      <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: teamColour(team, team?._index) }} aria-hidden="true" />
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <EntityAvatar entity={entity} name={title} size="lg" testid="entry-avatar" />
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold" data-testid="entry-title">{title}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground" data-testid="entry-meta">
              <span data-testid="entry-kind">{kindLabel}</span>
              <span aria-hidden="true">·</span>
              {team ? <Link to={`/teams/${team.id}`} className="hover:underline" data-testid="entry-team-link">{team.section || team.name}</Link> : <span>No team</span>}
              {model && <><span aria-hidden="true">·</span><span>{model}</span></>}
              {shapes?.length > 0 && <><span aria-hidden="true">·</span><ShapeIcons shapes={shapes} withLabels /></>}
              <StatusPill status={status} testid="entry-status" />
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FavouriteStar targetId={id} name={title} />
          <Button variant="secondary" asChild><Link to={`/feedback?about=${id}`} data-testid="entry-feedback-button"><MessageSquare className="!h-4 !w-4" /> Feedback</Link></Button>
          {canRun && <Button onClick={onRun} data-testid="entry-run-button"><Play className="!h-4 !w-4" /> Run</Button>}
        </div>
      </div>
    </header>
  );
};

const TABS = [["run", "Run"], ["about", "About"], ["needs", "What it needs"], ["prompts", "Prompts"], ["runs", "My runs"]];

export function useEntryTab(live, hasRun) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const key = `fcg-entry-tab-${user?.id}`;
  const allowed = TABS.filter(([t]) => t !== "run" || hasRun).map(([t]) => t);
  const pick = () => {
    const q = params.get("tab");
    if (allowed.includes(q)) return q;
    if (params.get("run") && hasRun) return "run";
    const saved = localStorage.getItem(key);
    if (saved && allowed.includes(saved) && (saved !== "run" || live)) return saved;
    return live && hasRun ? "run" : "about";
  };
  const [tab, setTabState] = useState(pick);
  const qTab = params.get("tab");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTabState(pick()); }, [qTab, live, hasRun]);
  const setTab = (t) => {
    setTabState(t);
    localStorage.setItem(key, t);
    if (params.get("tab")) { params.delete("tab"); setParams(params, { replace: true }); }
  };
  return { tab, setTab, tabs: TABS.filter(([t]) => allowed.includes(t)) };
}

export const EntryTabs = ({ tabs, tab, setTab }) => (
  <div role="tablist" aria-label="Sections" className="mb-6 flex flex-wrap gap-1 border-b" data-testid="entry-tabs">
    {tabs.map(([t, l]) => (
      <button key={t} role="tab" id={`tab-${t}`} aria-selected={tab === t} aria-controls={`panel-${t}`} onClick={() => setTab(t)} data-testid={`entry-tab-${t}`}
        onKeyDown={(e) => { const i = tabs.findIndex(([x]) => x === t); const n = e.key === "ArrowRight" ? i + 1 : e.key === "ArrowLeft" ? i - 1 : null; if (n !== null) { const next = tabs[(n + tabs.length) % tabs.length][0]; setTab(next); document.getElementById(`tab-${next}`)?.focus(); } }}
        className={cn("-mb-px border-b-2 px-3 py-2.5 text-sm transition-quick", tab === t ? "border-action font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{l}</button>
    ))}
  </div>
);

export const Panel = ({ id, tab, children, keepMounted }) => {
  const [seen, setSeen] = useState(tab === id);
  useEffect(() => { if (tab === id) setSeen(true); }, [tab, id]);
  if (!seen && !(keepMounted && tab === id)) return null;
  return <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} hidden={tab !== id} data-testid={`entry-panel-${id}`}>{children}</div>;
};

export const RunTab = ({ kind, entity, id, gates, guideExtraFields }) => {
  const [promptOpen, setPromptOpen] = useState(false);
  return (
    <div className="space-y-8">
      <RunSurface kind={kind} entity={entity} gates={gates} guideExtra={<SavedPromptPicker forId={id} />} guideFields={guideExtraFields} />
      <WhatNext fromId={id} onSavePrompt={() => setPromptOpen(true)} />
      {promptOpen && <AgentPrompts forId={id} formOpen={promptOpen} setFormOpen={setPromptOpen} hideList />}
    </div>
  );
};

export const PromptsTab = ({ id }) => {
  const [open, setOpen] = useState(false);
  return <AgentPrompts forId={id} formOpen={open} setFormOpen={setOpen} />;
};

export const MyRunsTab = ({ id, tab }) => <RunsPanel targetId={id} refreshKey={tab} />;
